import { observer } from 'mobx-react-lite';
import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { useStore } from '../../../app/stores/store';
import { Menu, Header, Dropdown, Input, Button, Label, Icon, Grid, Checkbox, Segment } from 'semantic-ui-react';
import Select, { SingleValue } from 'react-select';
import { sportOptions, createSportOptions } from "../../../app/common/options/sportOptions";
import { gameTypeOptions, createGameTypeOptions } from "../../../app/common/options/gameTypeOptions";
import { gamePaymentTypeOptions, createGamePaymentTypeOptions } from "../../../app/common/options/gamePaymentTypeOptions";
import { inclusionTypeOptions, createInclusionTypeOptions } from "../../../app/common/options/inclusionTypeOptions";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default observer(function GameFilters() {
    const { t } = useTranslation();
    const { gameStore: { predicate, setPredicate, loadGames, applyFilters }, userStore } = useStore();

    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const checkIfMobile = () => {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isSmallScreen = window.innerWidth <= 1024;
            setIsMobile(isTouchDevice && isSmallScreen);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Mode tracking
    type FilterMode = 'isGoing' | 'isHost' | 'isFollowing' | null;
    
    // Initialize all states from predicate
    const [selectedMode, setSelectedMode] = useState<FilterMode>(
        predicate.has('isGoing') ? 'isGoing' : 
        predicate.has('isHost') ? 'isHost' : 
        predicate.has('isFollowing') ? 'isFollowing' : null
    );

    // Create localized options
    const localizedSportOptions = createSportOptions(t, sportOptions);
    const localizedGameTypeOptions = createGameTypeOptions(t);
    const localizedGamePaymentTypeOptions = createGamePaymentTypeOptions(t);
    const localizedInclusionTypeOptions = createInclusionTypeOptions(t);

    // Find the selected sport option from predicate
    const findSportOption = () => {
        const sportValue = predicate.get('sport');
        if (!sportValue) return null;
        return localizedSportOptions.find(option => option.value === sportValue) || null;
    };

    // Sorting options
    const sortFieldOptions = [
        { key: 'date', text: t('gameFilters.sortFields.date'), value: 'date' },
        { key: 'distance', text: t('gameFilters.sortFields.distance'), value: 'distance' },
        { key: 'participants', text: t('gameFilters.sortFields.participants'), value: 'participants' }
    ];

    const sortDirectionOptions = [
        { key: 'asc', text: t('gameFilters.sortDirections.ascending'), value: 'asc' },
        { key: 'desc', text: t('gameFilters.sortDirections.descending'), value: 'desc' }
    ];

    // Local state to track filter values - all initialized from predicate
    const [selectedSport, setSelectedSport] = useState<SingleValue<{ value: string; label: string }>>(findSportOption());
    const [selectedGameType, setSelectedGameType] = useState<string | undefined>(predicate.get('gameType') || undefined);
    const [selectedPaymentType, setSelectedPaymentType] = useState<string | undefined>(predicate.get('payment') || undefined);
    const [selectedInclusionType, setSelectedInclusionType] = useState<string | undefined>(predicate.get('inclusion') || undefined);
    const [distance, setDistance] = useState(predicate.get('distance') ? Number(predicate.get('distance')) : 50);
    const [selectedSortField, setSelectedSortField] = useState<string | undefined>(predicate.get('sortField') || undefined);
    const [selectedSortDirection, setSelectedSortDirection] = useState<string | undefined>(predicate.get('sortDirection') || 'asc');
    const [filterChanged, setFilterChanged] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date>(predicate.get('startDate') || new Date());
    const [showCalendar, setShowCalendar] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false); // For mobile collapse/expand

    // NeedsOneMore filter - initialized from predicate
    const [needsOneMore, setNeedsOneMore] = useState<boolean>(
        predicate.get('needsOneMore') === 'true'
    );
    
    // Lock status filter with 3 options: All, Locked only, Unlocked only
    type LockStatusFilter = 'all' | 'locked' | 'unlocked';
    const [lockStatus, setLockStatus] = useState<LockStatusFilter>(
        predicate.has('isLocked') ? 
            predicate.get('isLocked') === 'true' ? 'locked' : 'unlocked' 
            : 'all'
    );

    // Check if user location is set
    const isUserLocationSet = userStore.user && 
        (userStore.user.latitude !== 0 || userStore.user.longitude !== 0);

    // Save current filter state to predicate when component unmounts
    useEffect(() => {
        return () => {
            // Don't modify predicate on unmount if filters have changed but not applied
            if (!filterChanged) return;
            
            // Otherwise, update predicate with current filter state
            if (selectedMode) {
                setPredicate(selectedMode, 'true');
            }
            
            if (selectedSport) {
                setPredicate('sport', selectedSport.value);
            }
            
            if (selectedGameType) {
                setPredicate('gameType', selectedGameType);
            }
            
            if (selectedPaymentType) {
                setPredicate('payment', selectedPaymentType);
            }
            
            if (selectedInclusionType) {
                setPredicate('inclusion', selectedInclusionType);
            }
            
            if (isUserLocationSet) {
                setPredicate('distance', String(distance));
            }
            
            // Set sorting options
            if (selectedSortField) {
                setPredicate('sortField', selectedSortField);
                setPredicate('sortDirection', selectedSortDirection || 'asc');
            }
            
            // Set lock status filter
            if (lockStatus !== 'all') {
                setPredicate('isLocked', lockStatus === 'locked' ? 'true' : 'false');
            }

            // Set needsOneMore filter
            if (needsOneMore) {
                setPredicate('needsOneMore', 'true');
            }
            
            setPredicate('startDate', selectedDate);
        };
    }, [selectedMode, selectedSport, selectedGameType, selectedPaymentType, 
        selectedInclusionType, distance, selectedDate, lockStatus, 
        selectedSortField, selectedSortDirection, needsOneMore, filterChanged]);

    // Handle mode selection (I'm going, I'm hosting, I'm following)
    const handleModeSelect = (mode: FilterMode) => {
        if (mode === selectedMode) {
            setSelectedMode(null);
        } else {
            setSelectedMode(mode);
        }
        setFilterChanged(true);
    };

    // Handle lock status change
    const handleLockStatusChange = (status: LockStatusFilter) => {
        setLockStatus(status);
        setFilterChanged(true);
    };

    // Handle the search button click to apply all filters at once
    const handleSearch = () => {
        // First reset all predicates
        setPredicate('reset', 'true');
        
        // Apply mode filter if selected
        if (selectedMode) {
            setPredicate(selectedMode, 'true');
        }
        
        // Apply sport filter if selected
        if (selectedSport) {
            setPredicate('sport', selectedSport.value);
        }
        
        // Apply game type filter if selected
        if (selectedGameType) {
            setPredicate('gameType', selectedGameType);
        }
        
        // Apply payment type filter if selected
        if (selectedPaymentType) {
            setPredicate('payment', selectedPaymentType);
        }
        
        // Apply inclusion type filter if selected
        if (selectedInclusionType) {
            setPredicate('inclusion', selectedInclusionType);
        }
        
        // Apply distance filter if user location is set
        if (isUserLocationSet) {
            setPredicate('distance', String(distance));
        }
        
        // Apply sort options
        if (selectedSortField) {
            setPredicate('sortField', selectedSortField);
            setPredicate('sortDirection', selectedSortDirection || 'asc');
        }
        
        // Apply lock status filter
        if (lockStatus !== 'all') {
            setPredicate('isLocked', lockStatus === 'locked' ? 'true' : 'false');
        }

        // Apply needsOneMore filter
        if (needsOneMore) {
            setPredicate('needsOneMore', 'true');
        }
        
        // Apply date filter
        setPredicate('startDate', selectedDate);
        
        // Reset the filter changed flag
        setFilterChanged(false);
        
        // Load games with all filters applied
        applyFilters();
        
        // Collapse on mobile after search
        if (isMobile) {
            setIsExpanded(false);
        }
    };

    // Reset all filters function
    const handleResetFilters = () => {
        setSelectedMode(null);
        setSelectedSport(null);
        setSelectedGameType(undefined);
        setSelectedPaymentType(undefined);
        setSelectedInclusionType(undefined);
        setDistance(50);
        setSelectedDate(new Date());
        setLockStatus('all');
        setSelectedSortField(undefined);
        setSelectedSortDirection('asc');
        setNeedsOneMore(false);
        setFilterChanged(true);
        setShowCalendar(false);
    };

    // Check if any filters are active
    const hasActiveFilters = selectedMode || selectedSport || selectedGameType || selectedPaymentType || 
        selectedInclusionType || lockStatus !== 'all' || selectedSortField || needsOneMore ||
        selectedDate.toDateString() !== new Date().toDateString();

    // Mobile compact view
    if (isMobile && !isExpanded) {
        return (
            <Segment style={{ marginBottom: '5px', padding: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon name='filter' color='teal' />
                        <span style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                            {t('gameFilters.title')}
                        </span>
                        {hasActiveFilters && (
                            <Label size='mini' color='teal' circular>
                                {[selectedMode, selectedSport, selectedGameType, selectedPaymentType, 
                                  selectedInclusionType, lockStatus !== 'all' ? lockStatus : null, 
                                  selectedSortField, needsOneMore ? 'needsOneMore' : null].filter(Boolean).length}
                            </Label>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <Button 
                            color='teal' 
                            onClick={handleSearch}
                            disabled={!filterChanged}
                            size='mini'
                            compact
                        >
                            <Icon name='search' />
                        </Button>
                        <Button 
                            onClick={() => setIsExpanded(true)}
                            size='mini'
                            compact
                        >
                            <Icon name='angle down' />
                        </Button>
                    </div>
                </div>
            </Segment>
        );
    }

    return (
        <Segment style={{ marginBottom: isMobile ? '5px' : '10px', padding: isMobile ? '6px' : '10px' }}>
            <Header as='h4' attached='top' color='teal' style={{ 
                padding: isMobile ? '4px 6px' : '6px 8px', 
                margin: 0,
                fontSize: isMobile ? '0.9rem' : '1rem'
            }}>
                <Icon name='filter' />
                {t('gameFilters.title')}
                {isMobile && (
                    <Button
                        floated='right'
                        size='mini'
                        compact
                        onClick={() => setIsExpanded(false)}
                        style={{ marginTop: '-2px' }}
                    >
                        <Icon name='angle up' />
                    </Button>
                )}
            </Header>
            
            <div style={{ padding: isMobile ? '6px' : '8px' }}>
                {/* Row 1: Mode tags and registration status */}
                <div style={{ marginBottom: isMobile ? '6px' : '8px' }}>
                    {/* Row 1a: Show games where (isGoing / isHost / isFollowing) */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <span style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', fontWeight: 'bold', minWidth: 'fit-content' }}>
                            {t('gameFilters.showGamesWhere')}:
                        </span>
                        <Label 
                            as='a' 
                            color={selectedMode === 'isGoing' ? 'teal' : 'grey'}
                            onClick={() => handleModeSelect('isGoing')}
                            size='mini'
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                        >
                            <Icon name='user' />
                            {t('gameFilters.modes.imGoing')}
                        </Label>
                        <Label 
                            as='a' 
                            color={selectedMode === 'isHost' ? 'teal' : 'grey'}
                            onClick={() => handleModeSelect('isHost')}
                            size='mini'
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                        >
                            <Icon name='star' />
                            {t('gameFilters.modes.imHosting')}
                        </Label>
                        <Label 
                            as='a' 
                            color={selectedMode === 'isFollowing' ? 'teal' : 'grey'}
                            onClick={() => handleModeSelect('isFollowing')}
                            size='mini'
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                        >
                            <Icon name='bookmark' />
                            {t('gameFilters.modes.imFollowing')}
                        </Label>
                    </div>
                    
                    {/* Row 1b: Registration / lock status */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <span style={{ fontSize: isMobile ? '0.75rem' : '0.85rem', fontWeight: 'bold', minWidth: 'fit-content' }}>
                            {t('gameFilters.registrationStatus')}:
                        </span>
                        <Label 
                            as='a' 
                            color={lockStatus === 'all' ? 'teal' : 'grey'}
                            onClick={() => handleLockStatusChange('all')}
                            size='mini'
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                        >
                            <Icon name='asterisk' />
                            {t('gameFilters.lockStatus.allGames')}
                        </Label>
                        <Label 
                            as='a' 
                            color={lockStatus === 'unlocked' ? 'teal' : 'grey'}
                            onClick={() => handleLockStatusChange('unlocked')}
                            size='mini'
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                        >
                            <Icon name='unlock' />
                            {t('gameFilters.lockStatus.open')}
                        </Label>
                        <Label 
                            as='a' 
                            color={lockStatus === 'locked' ? 'teal' : 'grey'}
                            onClick={() => handleLockStatusChange('locked')}
                            size='mini'
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                        >
                            <Icon name='lock' />
                            {t('gameFilters.lockStatus.locked')}
                        </Label>
                    </div>

                    {/* Row 1c: NeedsOneMore toggle */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Checkbox
                            toggle
                            label={
                                <label style={{
                                    fontSize: isMobile ? '0.75rem' : '0.85rem',
                                    fontWeight: needsOneMore ? 'bold' : 'normal',
                                    color: needsOneMore ? '#00b5ad' : 'inherit',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}>
                                    <Icon
                                        name='lightning'
                                        style={{ color: needsOneMore ? '#00b5ad' : '#999', fontSize: isMobile ? '0.75rem' : '0.85rem' }}
                                    />
                                    {t('gameFilters.needsOneMore.label')}
                                </label>
                            }
                            checked={needsOneMore}
                            onChange={(_, data) => {
                                setNeedsOneMore(data.checked ?? false);
                                setFilterChanged(true);
                            }}
                        />
                    </div>
                </div>
                
                {/* Row 2: Filter dropdowns */}
                {isMobile ? (
                    // Mobile: Stacked layout for better usability
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <div style={{ flex: '2', fontSize: '0.75rem' }}>
                                <Select
                                    options={localizedSportOptions}
                                    value={selectedSport}
                                    onChange={(option) => {
                                        setSelectedSport(option);
                                        setFilterChanged(true);
                                    }}
                                    placeholder={t('gameFilters.placeholders.selectSport')}
                                    isSearchable
                                    isClearable={true}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '24px',
                                            fontSize: '0.75rem'
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            fontSize: '0.75rem'
                                        })
                                    }}
                                />
                            </div>
                            <div style={{ flex: '1' }}>
                                <Dropdown
                                    fluid
                                    selection
                                    options={localizedGameTypeOptions}
                                    onChange={(e, { value }) => {
                                        setSelectedGameType(value as string);
                                        setFilterChanged(true);
                                    }}
                                    placeholder={t('gameFilters.placeholders.selectType')}
                                    clearable
                                    value={selectedGameType || ""}
                                    style={{ fontSize: '0.75rem', minHeight: '24px' }}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <div style={{ flex: '1' }}>
                                <Dropdown
                                    fluid
                                    selection
                                    options={localizedGamePaymentTypeOptions}
                                    onChange={(e, { value }) => {
                                        setSelectedPaymentType(value as string);
                                        setFilterChanged(true);
                                    }}
                                    placeholder={t('gameFilters.placeholders.freeOrPaid')}
                                    clearable
                                    value={selectedPaymentType || ""}
                                    style={{ fontSize: '0.75rem', minHeight: '24px' }}
                                />
                            </div>
                            <div style={{ flex: '1' }}>
                                <Dropdown
                                    fluid
                                    selection
                                    options={localizedInclusionTypeOptions}
                                    onChange={(e, { value }) => {
                                        setSelectedInclusionType(value as string);
                                        setFilterChanged(true);
                                    }}
                                    placeholder={t('gameFilters.placeholders.selectInclusion')}
                                    clearable
                                    value={selectedInclusionType || ""}
                                    style={{ fontSize: '0.75rem', minHeight: '24px' }}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                            <div style={{ flex: '1' }}>
                                <Dropdown
                                    fluid
                                    selection
                                    options={sortFieldOptions}
                                    onChange={(e, { value }) => {
                                        setSelectedSortField(value as string);
                                        setFilterChanged(true);
                                    }}
                                    placeholder={t('gameFilters.placeholders.sortBy')}
                                    value={selectedSortField || ""}
                                    clearable
                                    style={{ fontSize: '0.75rem', minHeight: '24px' }}
                                />
                            </div>
                            <div style={{ flex: '0 0 60px' }}>
                                <Dropdown
                                    fluid
                                    selection
                                    options={sortDirectionOptions}
                                    onChange={(e, { value }) => {
                                        setSelectedSortDirection(value as string);
                                        setFilterChanged(true);
                                    }}
                                    placeholder="↑↓"
                                    value={selectedSortDirection || "asc"}
                                    disabled={!selectedSortField}
                                    style={{ fontSize: '0.75rem', minHeight: '24px' }}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    // Desktop: Original grid layout
                    <Grid columns={6} stackable style={{ marginBottom: '8px' }}>
                        <Grid.Column width={3}>
                            <div style={{ fontSize: '0.8rem' }}>
                                <Select
                                    options={localizedSportOptions}
                                    value={selectedSport}
                                    onChange={(option) => {
                                        setSelectedSport(option);
                                        setFilterChanged(true);
                                    }}
                                    placeholder={t('gameFilters.placeholders.selectSport')}
                                    isSearchable
                                    isClearable={true}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '28px',
                                            fontSize: '0.8rem'
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            fontSize: '0.8rem'
                                        })
                                    }}
                                />
                            </div>
                        </Grid.Column>
                        
                        <Grid.Column width={2}>
                            <Dropdown
                                fluid
                                selection
                                options={localizedGameTypeOptions}
                                onChange={(e, { value }) => {
                                    setSelectedGameType(value as string);
                                    setFilterChanged(true);
                                }}
                                placeholder={t('gameFilters.placeholders.selectType')}
                                clearable
                                value={selectedGameType || ""}
                                style={{ fontSize: '0.8rem', minHeight: '28px' }}
                            />
                        </Grid.Column>
                        
                        <Grid.Column width={2}>
                            <Dropdown
                                fluid
                                selection
                                options={localizedGamePaymentTypeOptions}
                                onChange={(e, { value }) => {
                                    setSelectedPaymentType(value as string);
                                    setFilterChanged(true);
                                }}
                                placeholder={t('gameFilters.placeholders.freeOrPaid')}
                                clearable
                                value={selectedPaymentType || ""}
                                style={{ fontSize: '0.8rem', minHeight: '28px' }}
                            />
                        </Grid.Column>
                        
                        <Grid.Column width={2}>
                            <Dropdown
                                fluid
                                selection
                                options={localizedInclusionTypeOptions}
                                onChange={(e, { value }) => {
                                    setSelectedInclusionType(value as string);
                                    setFilterChanged(true);
                                }}
                                placeholder={t('gameFilters.placeholders.selectInclusion')}
                                clearable
                                value={selectedInclusionType || ""}
                                style={{ fontSize: '0.8rem', minHeight: '28px' }}
                            />
                        </Grid.Column>
                        
                        <Grid.Column width={2}>
                            <Dropdown
                                fluid
                                selection
                                options={sortFieldOptions}
                                onChange={(e, { value }) => {
                                    setSelectedSortField(value as string);
                                    setFilterChanged(true);
                                }}
                                placeholder={t('gameFilters.placeholders.sortBy')}
                                value={selectedSortField || ""}
                                clearable
                                style={{ fontSize: '0.8rem', minHeight: '28px' }}
                            />
                        </Grid.Column>
                        
                        <Grid.Column width={2}>
                            <Dropdown
                                fluid
                                selection
                                options={sortDirectionOptions}
                                onChange={(e, { value }) => {
                                    setSelectedSortDirection(value as string);
                                    setFilterChanged(true);
                                }}
                                placeholder="↑↓"
                                value={selectedSortDirection || "asc"}
                                disabled={!selectedSortField}
                                style={{ fontSize: '0.8rem', minHeight: '28px' }}
                            />
                        </Grid.Column>
                    </Grid>
                )}
                
                {/* Row 3: Date, distance and buttons */}
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: isMobile ? '6px' : '10px', 
                    flexWrap: 'wrap',
                    fontSize: isMobile ? '0.75rem' : '0.8rem'
                }}>
                    {/* Date selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span style={{ fontSize: isMobile ? '0.75rem' : '0.8rem', fontWeight: 'bold' }}>
                            {t('gameFilters.date')}:
                        </span>
                        <Button
                            size='mini'
                            color={showCalendar ? 'teal' : undefined}
                            onClick={() => setShowCalendar(!showCalendar)}
                            style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', padding: isMobile ? '4px 6px' : '6px 8px' }}
                            compact
                        >
                            <Icon name='calendar' />
                            {selectedDate.toLocaleDateString()}
                        </Button>
                    </div>
                    
                    {/* Distance slider - more compact on mobile */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '3px', 
                        minWidth: isMobile ? '120px' : '150px', 
                        flex: '1' 
                    }}>
                        <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {t('gameFilters.distance')}: {distance}km
                        </span>
                        <Input
                            type="range"
                            min={0}
                            max={300}
                            step={1}
                            value={distance}
                            onChange={(e) => {
                                const newDistance = Number(e.target.value);
                                setDistance(newDistance);
                                setFilterChanged(true);
                            }}
                            disabled={!isUserLocationSet}
                            style={{ flex: '1', maxWidth: isMobile ? '80px' : '100px' }}
                        />
                        {!isUserLocationSet && userStore.user && (
                            <Link to={`/profiles/${userStore.user?.userID}`}>
                                <Label color='red' size='mini'>
                                    <Icon name='location arrow' />
                                    {t('gameFilters.setLocation')}
                                </Label>
                            </Link>
                        )}
                    </div>
                    
                    {/* Action buttons */}
                    <div style={{ 
                        display: 'flex', 
                        gap: isMobile ? '4px' : '5px', 
                        flexShrink: 0,
                        flexWrap: isMobile ? 'wrap' : 'nowrap' 
                    }}>
                        <Button 
                            color='teal' 
                            onClick={handleSearch}
                            disabled={!filterChanged}
                            size={isMobile ? 'small' : 'mini'}
                            compact={!isMobile}
                            style={{ 
                                fontSize: isMobile ? '0.8rem' : '0.8rem',
                                padding: isMobile ? '8px 12px' : undefined,
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Icon name='search' />
                            {t('gameFilters.applyFilters')}
                        </Button>
                        <Button 
                            basic 
                            onClick={handleResetFilters}
                            size={isMobile ? 'small' : 'mini'}
                            compact={!isMobile}
                            style={{ 
                                fontSize: isMobile ? '0.8rem' : '0.8rem',
                                padding: isMobile ? '8px 12px' : undefined,
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Icon name='undo' />
                            {t('gameFilters.reset')}
                        </Button>
                    </div>
                </div>
                
                {/* Collapsible Calendar */}
                {showCalendar && (
                    <div style={{ marginTop: isMobile ? '6px' : '10px', maxWidth: isMobile ? '280px' : '300px' }}>
                        <Calendar
                            onChange={(date) => {
                                setSelectedDate(date as Date);
                                setFilterChanged(true);
                                setShowCalendar(false);
                            }}
                            value={selectedDate}
                            className="compact-calendar"
                        />
                        <style>{`
                            .compact-calendar {
                                font-size: ${isMobile ? '0.6rem' : '0.7rem'} !important;
                                max-width: ${isMobile ? '280px' : '300px'} !important;
                            }
                            .compact-calendar .react-calendar__tile {
                                height: ${isMobile ? '22px' : '25px'} !important;
                                padding: 0px 2px !important;
                                font-size: ${isMobile ? '0.6rem' : '0.7rem'} !important;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            }
                            .compact-calendar .react-calendar__navigation {
                                height: ${isMobile ? '24px' : '28px'} !important;
                            }
                            .compact-calendar .react-calendar__navigation button {
                                font-size: ${isMobile ? '0.7rem' : '0.75rem'} !important;
                                padding: 0 !important;
                                height: ${isMobile ? '24px' : '28px'} !important;
                            }
                        `}</style>
                    </div>
                )}
            </div>
        </Segment>
    );
});